import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, Plus, Pencil, Trash2, X, Inbox, ShoppingBag, Package, ShieldAlert, ImagePlus, CheckCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Logo from "@/components/autobay/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

const EMPTY_PRODUCT = { name: "", category: "", price: "", rating: "5.0", image: "" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (src) => (src?.startsWith("/api") ? `${BACKEND}${src}` : src);
const inputCls =
  "w-full bg-[#0c0c0f] border border-white/10 focus:border-red-500/70 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors";

function fmtDate(ts) {
  try {
    return new Date(ts).toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return ts;
  }
}

export default function Admin() {
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("zahtevi");
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    if (window.location.hash?.includes("session_id=")) return;
    ax.get("/auth/me")
      .then((r) => { setUser(r.data); setAuth(true); })
      .catch(() => setAuth(false));
  }, [location.state]);

  const loadData = () => {
    ax.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    ax.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    ax.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (auth && user?.is_admin) loadData();
  }, [auth, user]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await ax.post("/auth/logout").catch(() => {});
    setAuth(false);
    setUser(null);
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, category: p.category, price: p.price, rating: p.rating, image: p.image });
    setImageFile(null);
    setImagePreview(p.image ? imgUrl(p.image) : "");
  };

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const resetForm = () => {
    setForm(EMPTY_PRODUCT);
    setEditing(null);
    setImageFile(null);
    setImagePreview("");
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!editing && !imageFile) {
      toast.error("Slika proizvoda je obavezna — izaberite fajl");
      return;
    }
    setSaving(true);
    try {
      let image = form.image;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const up = await ax.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image = up.data.url;
      }
      const payload = { ...form, image };
      if (editing) {
        await ax.put(`/admin/products/${editing}`, payload);
        toast.success("Proizvod izmenjen");
      } else {
        await ax.post("/admin/products", payload);
        toast.success("Proizvod dodat");
      }
      resetForm();
      const r = await ax.get("/products");
      setProducts(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri čuvanju proizvoda");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    try {
      await ax.delete(`/admin/products/${id}`);
      setProducts(products.filter((p) => p.id !== id));
      toast.success("Proizvod obrisan");
    } catch {
      toast.error("Greška pri brisanju");
    }
  };

  const toggleStatus = async (kind, item) => {
    const next = item.status === "zavrseno" ? "novo" : "zavrseno";
    try {
      await ax.patch(`/admin/${kind}/${item.id}/status`, { status: next });
      if (kind === "bookings") setBookings(bookings.map((b) => (b.id === item.id ? { ...b, status: next } : b)));
      else setOrders(orders.map((o) => (o.id === item.id ? { ...o, status: next } : o)));
      toast.success(next === "zavrseno" ? "Označeno kao završeno" : "Vraćeno u nove");
    } catch {
      toast.error("Greška pri izmeni statusa");
    }
  };

  const removeItem = async (kind, id) => {
    if (!window.confirm("Obrisati trajno? Ova akcija se ne može poništiti.")) return;
    try {
      await ax.delete(`/admin/${kind}/${id}`);
      if (kind === "bookings") setBookings(bookings.filter((b) => b.id !== id));
      else setOrders(orders.filter((o) => o.id !== id));
      toast.success("Stavka obrisana");
    } catch {
      toast.error("Greška pri brisanju");
    }
  };

  const statusBadge = (status) =>
    status === "zavrseno" ? (
      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-2.5 py-1 rounded-full">Završeno</span>
    ) : (
      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/25 px-2.5 py-1 rounded-full">Novo</span>
    );

  const itemActions = (kind, item) => (
    <div className="flex items-center gap-2">
      <button
        data-testid={`admin-${kind}-toggle-${item.id}`}
        onClick={() => toggleStatus(kind, item)}
        title={item.status === "zavrseno" ? "Vrati u nove" : "Označi kao završeno"}
        className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full border transition-all active:scale-[0.96] ${
          item.status === "zavrseno"
            ? "border-white/15 text-zinc-400 hover:text-white"
            : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
        }`}
      >
        {item.status === "zavrseno" ? <RotateCcw size={13} /> : <CheckCheck size={13} />}
        {item.status === "zavrseno" ? "Vrati" : "Završi"}
      </button>
      <button
        data-testid={`admin-${kind}-delete-${item.id}`}
        onClick={() => removeItem(kind, item.id)}
        title="Obriši"
        className="p-2 rounded-full border border-white/10 hover:border-red-500/60 text-zinc-500 hover:text-red-400 transition-colors"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );

  const TABS = [
    { id: "zahtevi", label: "Zahtevi", icon: Inbox, count: bookings.length },
    { id: "narudzbine", label: "Narudžbine", icon: ShoppingBag, count: orders.length },
    { id: "proizvodi", label: "Proizvodi", icon: Package, count: products.length },
  ];

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#09090B] text-white">
      <header className="flex items-center justify-between px-5 lg:px-10 py-4 border-b border-white/[0.07] bg-[rgba(9,9,11,0.9)] backdrop-blur-md sticky top-0 z-40">
        <Link to="/" data-testid="admin-logo" className="flex items-center gap-3">
          <Logo size={34} />
          <span className="font-display text-xl tracking-wider">AUTO<span className="text-red-500">BAY</span>
            <span className="text-zinc-500 text-xs font-sans tracking-normal ml-2">Admin Panel</span>
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm text-zinc-400 hover:text-white transition-colors">← Nazad na sajt</Link>
          {user && (
            <button data-testid="admin-logout-button" onClick={logout}
              className="flex items-center gap-2 text-sm border border-white/15 hover:border-red-500/60 hover:bg-red-600/10 px-4 py-2 rounded-full transition-all">
              <LogOut size={14} /> Odjava
            </button>
          )}
        </div>
      </header>

      {auth === null && (
        <div className="flex justify-center py-32"><Loader2 size={32} className="animate-spin text-red-500" /></div>
      )}

      {auth === false && (
        <div className="flex flex-col items-center justify-center py-28 px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="bg-[#121216] border border-white/[0.07] rounded-3xl p-10 max-w-md w-full">
            <Logo size={52} />
            <h1 className="font-display text-3xl mt-6">Admin <span className="text-red-500">Prijava</span></h1>
            <p className="text-sm text-zinc-500 mt-3 leading-relaxed">
              Pristup je dozvoljen samo administratorima. Prijavite se svojim Google nalogom.
            </p>
            <button data-testid="admin-google-login-button" onClick={login}
              className="mt-8 w-full flex items-center justify-center gap-3 bg-white text-zinc-900 font-semibold py-3.5 rounded-full hover:bg-zinc-200 transition-colors active:scale-[0.98]">
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.6z"/><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.1 1.2-3.2 0-5.9-2.1-6.8-5l-3.9 3C3.3 21.3 7.3 24 12 24z"/><path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-3.9-3C.5 8.3 0 10.1 0 12s.5 3.7 1.3 5.4l3.9-3z"/><path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.6l3.9 3c.9-2.8 3.6-4.9 6.8-4.9z"/></svg>
              Prijavi se preko Google-a
            </button>
          </motion.div>
        </div>
      )}

      {auth === true && user && !user.is_admin && (
        <div className="flex flex-col items-center justify-center py-28 px-6 text-center">
          <ShieldAlert size={40} className="text-red-500" />
          <h1 className="font-display text-3xl mt-5">Pristup Odbijen</h1>
          <p className="text-sm text-zinc-500 mt-3 max-w-sm">
            Nalog <span className="text-white">{user.email}</span> nema administratorska prava.
            Obratite se vlasniku sajta.
          </p>
          <button onClick={logout} className="mt-6 text-sm text-red-400 hover:text-red-300">Prijavite se drugim nalogom</button>
        </div>
      )}

      {auth === true && user?.is_admin && (
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-display text-4xl">Zdravo, <span className="text-red-500">{user.name?.split(" ")[0] || "Admin"}</span></h1>
              <p className="text-sm text-zinc-500 mt-1">{user.email}</p>
            </div>
            <button data-testid="admin-refresh-button" onClick={loadData}
              className="text-sm border border-white/15 hover:border-red-500/60 px-4 py-2 rounded-full transition-all">
              Osveži podatke
            </button>
          </div>

          <div data-testid="admin-tabs" className="flex gap-2 mt-8 border-b border-white/[0.07] pb-px">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-t-xl border-b-2 transition-colors ${
                  tab === t.id ? "border-red-500 text-white bg-red-600/10" : "border-transparent text-zinc-500 hover:text-white"
                }`}>
                <t.icon size={15} /> {t.label}
                <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full">{t.count}</span>
              </button>
            ))}
          </div>

          {tab === "zahtevi" && (
            <div data-testid="admin-bookings-list" className="mt-6 space-y-3">
              {bookings.length === 0 && <p className="text-zinc-500 text-sm py-10 text-center">Nema zahteva za sada.</p>}
              {bookings.map((b) => (
                <div key={b.id} data-testid={`admin-booking-${b.id}`}
                  className={`bg-[#121216] border rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4 transition-opacity ${
                    b.status === "zavrseno" ? "border-white/[0.05] opacity-60" : "border-white/[0.07]"
                  }`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold">{b.name}</span>
                      {statusBadge(b.status)}
                      <span className="text-xs text-red-400 bg-red-600/10 border border-red-600/20 px-2.5 py-0.5 rounded-full">{b.service_type}</span>
                    </div>
                    <div className="text-sm text-zinc-400 mt-1.5">
                      <a href={`tel:${b.phone}`} className="text-white hover:text-red-400">{b.phone}</a>
                      {b.preferred_date && <span className="text-zinc-500"> · {b.preferred_date}</span>}
                    </div>
                    {b.message && <p className="text-sm text-zinc-500 mt-1.5">{b.message}</p>}
                  </div>
                  <div className="flex md:flex-col items-center md:items-end gap-3 shrink-0">
                    <span className="text-xs text-zinc-600">{fmtDate(b.timestamp)}</span>
                    {itemActions("bookings", b)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "narudzbine" && (
            <div data-testid="admin-orders-list" className="mt-6 space-y-3">
              {orders.length === 0 && <p className="text-zinc-500 text-sm py-10 text-center">Nema narudžbina za sada.</p>}
              {orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-${o.id}`}
                  className={`bg-[#121216] border rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4 transition-opacity ${
                    o.status === "zavrseno" ? "border-white/[0.05] opacity-60" : "border-white/[0.07]"
                  }`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold">{o.product_name}</span>
                      {statusBadge(o.status)}
                      <span className="text-xs text-red-400 bg-red-600/10 border border-red-600/20 px-2.5 py-0.5 rounded-full">{o.price}</span>
                    </div>
                    <div className="text-sm text-zinc-400 mt-1.5">
                      {o.customer_name} · <a href={`tel:${o.customer_phone}`} className="text-white hover:text-red-400">{o.customer_phone}</a>
                    </div>
                    {o.customer_address && (
                      <div className="text-sm text-zinc-500 mt-1">
                        {o.customer_address} · Dostava: BexExpress
                      </div>
                    )}
                  </div>
                  <div className="flex md:flex-col items-center md:items-end gap-3 shrink-0">
                    <span className="text-xs text-zinc-600">{fmtDate(o.timestamp)}</span>
                    {itemActions("orders", o)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "proizvodi" && (
            <div className="mt-6 grid lg:grid-cols-[380px_1fr] gap-8 items-start">
              <form data-testid="admin-product-form" onSubmit={saveProduct}
                className="bg-[#121216] border border-white/[0.07] rounded-2xl p-6 space-y-4 lg:sticky lg:top-24">
                <h3 className="font-display text-xl">{editing ? "Izmeni Proizvod" : "Dodaj Proizvod"}</h3>
                <input data-testid="product-form-name" placeholder="Naziv proizvoda *" value={form.name} required
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                <input data-testid="product-form-category" placeholder="Kategorija (npr. Motorna Ulja) *" value={form.category} required
                  onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="product-form-price" placeholder="Cena (npr. 4.200 RSD) *" value={form.price} required
                    onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
                  <input data-testid="product-form-rating" placeholder="Ocena (npr. 4.8)" value={form.rating}
                    onChange={(e) => setForm({ ...form, rating: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">
                    Slika proizvoda {editing ? "(ostavi prazno da zadržiš postojeću)" : "*"}
                  </label>
                  <label data-testid="product-form-image-upload"
                    className="flex items-center justify-center gap-3 border-2 border-dashed border-white/15 hover:border-red-500/60 rounded-xl px-4 py-4 cursor-pointer transition-colors text-sm text-zinc-400 hover:text-white">
                    <ImagePlus size={18} className="text-red-500 shrink-0" />
                    {imageFile ? imageFile.name : "Izaberi sliku sa uređaja (PNG, JPG, WEBP — max 5 MB)"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pickImage} />
                  </label>
                  {imagePreview && (
                    <img src={imagePreview} alt="Pregled" className="mt-3 h-24 w-full object-cover rounded-xl border border-white/10" />
                  )}
                </div>
                <div className="flex gap-3">
                  <button data-testid="product-form-submit" type="submit" disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-60 font-semibold py-3 rounded-full transition-all active:scale-[0.98]">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : editing ? <Pencil size={15} /> : <Plus size={16} />}
                    {editing ? "Sačuvaj Izmene" : "Dodaj"}
                  </button>
                  {editing && (
                    <button type="button" data-testid="product-form-cancel" onClick={resetForm}
                      className="px-4 border border-white/15 rounded-full hover:border-red-500/60 transition-colors">
                      <X size={16} />
                    </button>
                  )}
                </div>
              </form>

              <div data-testid="admin-products-list" className="grid sm:grid-cols-2 gap-4">
                {products.map((p) => (
                  <div key={p.id} data-testid={`admin-product-${p.id}`}
                    className="bg-[#121216] border border-white/[0.07] rounded-2xl overflow-hidden flex">
                    {p.image && <img src={imgUrl(p.image)} alt={p.name} className="w-24 h-24 object-cover shrink-0" />}
                    <div className="p-4 flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-red-400/80 font-bold">{p.category}</div>
                      <div className="font-semibold text-sm truncate mt-0.5">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-1">{p.price} · ★ {p.rating}</div>
                    </div>
                    <div className="flex flex-col justify-center gap-1.5 pr-3">
                      <button data-testid={`admin-product-edit-${p.id}`} onClick={() => startEdit(p)}
                        className="p-2 rounded-lg border border-white/10 hover:border-red-500/60 text-zinc-400 hover:text-white transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button data-testid={`admin-product-delete-${p.id}`} onClick={() => deleteProduct(p.id)}
                        className="p-2 rounded-lg border border-white/10 hover:border-red-500/60 text-zinc-400 hover:text-red-400 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
